import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type { ReleaseDateChangeView, ReleaseDetail } from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Drawer,
  Field,
  InlineAlert,
  Input,
  StatusBadge,
  Textarea,
} from "../../shared/ui";
import { formatReleaseMoment } from "./releaseVocabulary";

/**
 * When the release is going out, and what it takes to change that.
 *
 * <p>The date is the one fact the rest of the organisation plans around — other teams schedule
 * against it, customers are told it, work is sequenced by it. So it is not an editable field
 * here: moving it is a request that carries a reason, and Management, the project manager, a
 * team lead or an administrator decides it. Any one of them is enough; a date that waits for
 * four signatures waits for whichever of them is on leave.
 *
 * <p>An outstanding request is shown to everybody, not just whoever can decide it. Somebody
 * planning around this date should be able to see that it is under question before they find out
 * from the answer.
 */
export function ReleaseDateCard({
  release,
  onChanged,
}: {
  release: ReleaseDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [deciding, setDeciding] = useState<"approve" | "decline" | null>(null);

  const changes = useQuery({
    queryKey: ["release-date-changes", release.id],
    queryFn: () =>
      api.get<ReleaseDateChangeView[]>(`/api/v1/releases/${release.id}/date-changes`),
  });

  const pending = (changes.data ?? []).find((change) => change.status === "PENDING");
  const settled = (changes.data ?? []).filter((change) => change.status !== "PENDING");
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["release-date-changes", release.id] });
    onChanged();
  };

  return (
    <>
      <Card>
        <CardHeader
          title="Going out on"
          subtitle="What everybody else is planning around."
          action={
            can("RELEASE_EDIT") && !pending && (
              <Button
                variant="secondary"
                size="sm"
                icon={<CalendarClock className="h-4 w-4" />}
                onClick={() => setAsking(true)}
              >
                Ask to move it
              </Button>
            )
          }
        />
        <CardBody className="space-y-4">
          <p className="font-display text-xl font-semibold text-ink-900">
            {release.plannedReleaseAt
              ? formatReleaseMoment(release.plannedReleaseAt, release.timeZone)
              : "No date set"}
          </p>

          {pending && (
            <InlineAlert tone="warning">
              <div className="space-y-2">
                <p>
                  <span className="font-medium">{pending.requestedByName}</span> has asked to move
                  this to{" "}
                  <span className="font-medium">
                    {formatReleaseMoment(pending.newPlannedAt, release.timeZone)}
                  </span>
                  . It does not move until somebody decides.
                </p>
                <p className="text-ink-700">“{pending.reason}”</p>
                {/* Shown to everybody; actionable only by whoever can decide it. */}
                {can("RELEASE_DATE_APPROVE") && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button size="sm" onClick={() => setDeciding("approve")}>
                      Approve the move
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setDeciding("decline")}
                    >
                      Decline
                    </Button>
                  </div>
                )}
              </div>
            </InlineAlert>
          )}

          {/* The record of what nearly was, which is the part a history line never kept. */}
          {settled.length > 0 && (
            <ul className="space-y-2 border-t border-ink-100 pt-3">
              {settled.map((change) => (
                <li key={change.id} className="text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge tone={change.status === "APPROVED" ? "green" : "gray"}>
                      {change.status === "APPROVED" ? "Moved" : "Declined"}
                    </StatusBadge>
                    <span className="text-ink-600">
                      {formatReleaseMoment(change.newPlannedAt, release.timeZone)}
                    </span>
                    <span className="text-xs text-ink-500">
                      asked by {change.requestedByName}
                      {change.decidedByName && `, decided by ${change.decidedByName}`}
                    </span>
                  </div>
                  <p className="text-xs text-ink-500">“{change.reason}”</p>
                  {change.decisionNote && (
                    <p className="text-xs text-ink-500">— {change.decisionNote}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <AskDrawer
        open={asking}
        release={release}
        onClose={() => setAsking(false)}
        onSaved={refresh}
      />
      <DecideDrawer
        open={deciding != null}
        decision={deciding ?? "approve"}
        request={pending ?? null}
        timeZone={release.timeZone}
        onClose={() => setDeciding(null)}
        onSaved={refresh}
      />
    </>
  );
}

/** Asking. The reason is required, because it is the whole of what the decision is made from. */
function AskDrawer({
  open,
  release,
  onClose,
  onSaved,
}: {
  open: boolean;
  release: ReleaseDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [when, setWhen] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/releases/${release.id}/date-change`, {
        plannedReleaseAt: new Date(when).toISOString(),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      setWhen("");
      setReason("");
      setError(null);
      onSaved();
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "Could not send the request."),
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Ask to move the release date"
      subtitle="Management, the project manager, a team lead or an administrator decides it. Any one of them is enough."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => ask.mutate()}
            disabled={when === "" || reason.trim() === ""}
            loading={ask.isPending}
          >
            Send the request
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="Currently going out on">
          <p className="text-sm text-ink-600">
            {release.plannedReleaseAt
              ? formatReleaseMoment(release.plannedReleaseAt, release.timeZone)
              : "No date set"}
          </p>
        </Field>

        <Field label="Move it to" required>
          <Input
            type="datetime-local"
            value={when}
            onChange={(event) => setWhen(event.target.value)}
          />
        </Field>

        <Field
          label="Why"
          required
          hint="Whoever decides this has only what you write here to decide from."
        >
          <Textarea
            rows={4}
            value={reason}
            placeholder="The bank sandbox is not available until the 12th."
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}

/** Deciding. Approving is what actually moves the date. */
function DecideDrawer({
  open,
  decision,
  request,
  timeZone,
  onClose,
  onSaved,
}: {
  open: boolean;
  decision: "approve" | "decline";
  request: ReleaseDateChangeView | null;
  timeZone: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const approving = decision === "approve";

  const decide = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/release-date-changes/${request?.id}/decide`, {
        approved: approving,
        note: note.trim() === "" ? null : note.trim(),
      }),
    onSuccess: () => {
      setNote("");
      setError(null);
      onSaved();
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "Could not record the decision."),
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={approving ? "Approve the move" : "Decline the move"}
      subtitle={
        approving
          ? "The date moves when you approve, and everybody on the project is told."
          : "The date stays as it is. Only the person who asked is told."
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => decide.mutate()} loading={decide.isPending}>
            {approving ? "Approve" : "Decline"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {request && (
          <>
            <Field label="Asked for">
              <p className="text-sm text-ink-600">
                {formatReleaseMoment(request.oldPlannedAt, timeZone)} →{" "}
                <span className="font-medium text-ink-900">
                  {formatReleaseMoment(request.newPlannedAt, timeZone)}
                </span>
              </p>
            </Field>
            <Field label="Reason given">
              <p className="text-sm text-ink-700">“{request.reason}”</p>
            </Field>
          </>
        )}

        <Field
          label="Note"
          hint={
            approving
              ? "Optional. Anything the project should know about the new date."
              : "Worth writing: the person who asked will read this and nothing else."
          }
        >
          <Textarea
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}
